import * as THREE from "three";
import { CSS2DObject, type CSS2DRenderer } from "three/addons/renderers/CSS2DRenderer.js";
import { PlanetAudio } from "./audio";
import { CameraRig } from "./camera-rig";
import { LANDMARKS, NPCS, landmarkById, type NpcId } from "./catalog";
import { createNpc, createLandmark, type TickFn } from "./figures";
import { Input, preferVirtualJoystick } from "./input";
import { createLights, createPlanet, createRenderer, createStarfield } from "./planet";
import { Player } from "./player";
import { loadSave, unlockBadge, unlockPostcard, type SaveState } from "./save";
import {
  INTERACT_RADIUS,
  LANDMARK_RADIUS,
  NPC_SPEED,
  PLANET_RADIUS,
  PLAYER_SPEED,
  facingCamera,
  greatCircleStep,
  moveOnSphere,
  orientOnSphere,
  surfaceDistance,
} from "./sphere";
import { GameUI } from "./ui";

export class Game {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly labels: CSS2DRenderer;
  private readonly clock = new THREE.Clock();
  private readonly input: Input;
  private readonly ui: GameUI;
  private readonly audio = new PlanetAudio();
  private readonly player: Player;
  private readonly rig: CameraRig;
  private readonly stars: THREE.Points;
  private readonly landmarks = new Map<string, THREE.Group>();
  private readonly npcs = new Map<NpcId, THREE.Group>();
  private readonly guiding = new Set<NpcId>();
  private readonly save: SaveState;
  private readonly fwd = new THREE.Vector3();
  private readonly right = new THREE.Vector3();
  private readonly moveDir = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private started = false;
  private introYaw = 0.6;
  private raf = 0;
  private nearbyNpc: NpcId | null = null;

  constructor(private readonly viewport: HTMLElement) {
    this.save = loadSave();
    this.ui = new GameUI(this.save);
    this.audio.setMuted(this.save.muted);
    const { renderer, labels } = createRenderer(viewport);
    this.renderer = renderer;
    this.labels = labels;
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 400);
    this.rig = new CameraRig(this.camera);
    this.input = new Input(
      renderer.domElement,
      document.getElementById("joystick") as HTMLElement,
      document.getElementById("joystick-knob") as HTMLElement,
    );
    this.input.showJoystick(preferVirtualJoystick());

    createLights(this.scene);
    this.scene.add(createPlanet());
    this.stars = createStarfield();
    this.scene.add(this.stars);

    for (const def of LANDMARKS) {
      const mesh = createLandmark(def);
      this.landmarks.set(def.id, mesh);
      this.scene.add(mesh);
    }
    for (const def of NPCS) {
      const mesh = createNpc(def);
      this.npcs.set(def.id, mesh);
      this.scene.add(mesh);
    }

    this.player = new Player();
    this.scene.add(this.player.group);

    this.ui.onEnter = () => void this.enter();
    this.ui.onTalk = (id) => this.ui.openTalk(id);
    this.ui.onGuide = (id) => this.guiding.add(id);
    this.ui.onMute = (muted) => this.audio.setMuted(muted);

    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.onGlobalKey);
    this.onResize();
    this.raf = requestAnimationFrame(this.loop);
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("keydown", this.onGlobalKey);
    this.input.dispose();
    this.audio.dispose();
    this.renderer.dispose();
  }

  private async enter(): Promise<void> {
    this.started = true;
    this.ui.startPlay();
    this.input.enabled = true;
    this.audio.setMuted(this.save.muted);
    await this.audio.start();
  }

  private onGlobalKey = (event: KeyboardEvent): void => {
    if (event.code === "Escape") {
      this.ui.closeModals();
      return;
    }
    if (!this.started) {
      return;
    }
    if (event.code === "KeyM") {
      this.ui.toggleMute();
    }
    if (event.code === "KeyC" || event.code === "KeyI") {
      this.ui.toggleDrawer();
    }
    if (event.code === "KeyE" && this.nearbyNpc && !this.ui.blocking) {
      this.ui.openTalk(this.nearbyNpc);
    }
  };

  private onResize = (): void => {
    const w = this.viewport.clientWidth || window.innerWidth;
    const h = this.viewport.clientHeight || window.innerHeight;
    this.camera.aspect = w / Math.max(h, 1);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
    this.labels.setSize(w, h);
    this.input.showJoystick(preferVirtualJoystick());
  };

  private loop = (): void => {
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const time = this.clock.elapsedTime;
    this.stars.rotation.y += dt * 0.003;

    if (!this.started) {
      this.introYaw += dt * 0.12;
      this.camera.position.set(Math.cos(this.introYaw) * 34, 12, Math.sin(this.introYaw) * 34);
      this.camera.up.set(0, 1, 0);
      this.camera.lookAt(0, 0, 0);
      this.render();
      return;
    }

    this.input.enabled = !this.ui.blocking;
    const orbit = this.input.consumeOrbit();
    if (!this.ui.blocking) {
      this.rig.orbit(orbit.x, orbit.y);
      this.rig.zoom(this.input.consumeWheel());
    }
    this.stepPlayer(dt);
    this.rig.follow(this.player.position);
    this.stepNpcs(dt, time);
    this.player.update(dt, time);
    this.checkProximity();
    this.updateLabels();
    this.input.endFrame();
    this.render();
  };

  private stepPlayer(dt: number): void {
    const { x, y } = this.input.move;
    this.up.copy(this.player.position).normalize();
    if (x === 0 && y === 0) {
      this.player.moving = false;
      this.player.group.position.normalize().multiplyScalar(PLANET_RADIUS);
      return;
    }
    this.camera.getWorldDirection(this.fwd);
    this.fwd.projectOnPlane(this.up);
    if (this.fwd.lengthSq() < 1e-8) {
      this.player.moving = false;
      return;
    }
    this.fwd.normalize();
    this.right.crossVectors(this.fwd, this.up).normalize();
    this.moveDir.copy(this.fwd).multiplyScalar(y).addScaledVector(this.right, x);
    if (this.moveDir.lengthSq() < 1e-8) {
      this.player.moving = false;
      return;
    }
    this.moveDir.normalize();
    const next = moveOnSphere(this.player.position, this.moveDir, PLAYER_SPEED * dt, PLANET_RADIUS);
    orientOnSphere(this.player.group, next, this.moveDir);
    this.player.moving = true;
  }

  private stepNpcs(dt: number, time: number): void {
    for (const def of NPCS) {
      const group = this.npcs.get(def.id);
      if (!group) {
        continue;
      }
      const figure = group.children[0];
      const tick = figure?.userData.tick as TickFn | undefined;
      const moving = this.guiding.has(def.id);
      tick?.(time, moving);
      if (!moving) {
        continue;
      }
      const landmark = this.landmarks.get(def.guideTo);
      if (!landmark) {
        this.guiding.delete(def.id);
        continue;
      }
      const dist = surfaceDistance(group.position, landmark.position, PLANET_RADIUS);
      if (dist < 1.5) {
        this.guiding.delete(def.id);
        continue;
      }
      const next = greatCircleStep(group.position, landmark.position, NPC_SPEED * dt, PLANET_RADIUS);
      const face = landmark.position.clone().sub(group.position);
      orientOnSphere(group, next, face);
    }
  }

  private checkProximity(): void {
    let bestNpc: NpcId | null = null;
    let bestDist = INTERACT_RADIUS;
    for (const def of NPCS) {
      const group = this.npcs.get(def.id);
      if (!group) {
        continue;
      }
      const dist = surfaceDistance(this.player.position, group.position, PLANET_RADIUS);
      if (dist < bestDist) {
        bestDist = dist;
        bestNpc = def.id;
      }
      if (dist < INTERACT_RADIUS && unlockBadge(this.save, def.id)) {
        this.ui.refresh(this.save);
        this.ui.enqueueCollect({ kind: "badge", id: def.id });
      }
    }

    let landmarkHint: string | null = null;
    for (const def of LANDMARKS) {
      const group = this.landmarks.get(def.id);
      if (!group) {
        continue;
      }
      const dist = surfaceDistance(this.player.position, group.position, PLANET_RADIUS);
      if (dist < LANDMARK_RADIUS) {
        landmarkHint = `${def.name} · ${def.subtitle}`;
        if (unlockPostcard(this.save, def.id)) {
          this.ui.refresh(this.save);
          this.ui.enqueueCollect({ kind: "postcard", id: def.id });
        }
      }
    }

    this.nearbyNpc = bestNpc;
    if (this.ui.blocking) {
      this.ui.setHint(null, null);
      return;
    }
    if (bestNpc) {
      const dest = landmarkById(NPCS.find((n) => n.id === bestNpc)?.guideTo ?? "workshop");
      const guiding = this.guiding.has(bestNpc);
      this.ui.setHint(
        guiding ? `${bestNpcName(bestNpc)} 正在带路：${dest.name}` : `${bestNpcName(bestNpc)} · 按 E 交谈`,
        bestNpc,
      );
      return;
    }
    this.ui.setHint(landmarkHint, null);
  }

  private readonly labelWorld = new THREE.Vector3();

  private updateLabels(): void {
    const cam = this.camera.position;
    const visit = (obj: THREE.Object3D): void => {
      for (const child of obj.children) {
        if (child instanceof CSS2DObject) {
          child.getWorldPosition(this.labelWorld);
          child.element.style.opacity = facingCamera(this.labelWorld, cam) > 0.12 ? "1" : "0";
        }
        visit(child);
      }
    };
    visit(this.scene);
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
    this.labels.render(this.scene, this.camera);
  }
}

function bestNpcName(id: NpcId): string {
  return NPCS.find((item) => item.id === id)?.name ?? id;
}
